import { useState } from 'react';
import { Button } from '../ui/button';
import { canPerformAction } from '../../utils/permissions';

interface AdminActionsProps {
  userRole?: string;
  resource: string;
  onEdit?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onExport?: () => void;
  itemName?: string;
  className?: string;
}

export function AdminActions({
  userRole,
  resource,
  onEdit,
  onDelete,
  onDuplicate,
  onExport,
  itemName = 'item',
  className = ''
}: AdminActionsProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const canEdit = canPerformAction(userRole, resource, 'update');
  const canDelete = canPerformAction(userRole, resource, 'delete');
  const canCreate = canPerformAction(userRole, resource, 'create');
  const canManage = canPerformAction(userRole, resource, 'manage');

  if (!canEdit && !canDelete && !canCreate && !canManage) {
    return null;
  }

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Edit Action */}
      {canEdit && onEdit && (
        <Button
          variant="outline"
          size="sm"
          onClick={onEdit}
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit
        </Button>
      )}

      {/* Duplicate Action */}
      {canCreate && onDuplicate && (
        <Button
          variant="outline"
          size="sm"
          onClick={onDuplicate}
          className="text-green-600 hover:text-green-700 hover:bg-green-50"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Duplicate
        </Button>
      )}

      {/* Export Action */}
      {canManage && onExport && (
        <Button
          variant="outline"
          size="sm"
          onClick={onExport}
          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export
        </Button>
      )}

      {/* Delete Action */}
      {canDelete && onDelete && (
        <>
          {!showDeleteConfirm ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-600 font-medium">Delete {itemName}?</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Confirm
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-600 hover:text-gray-700"
              >
                Cancel
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface BulkActionsProps {
  userRole?: string;
  resource: string;
  selectedCount: number;
  onBulkEdit?: () => void;
  onBulkDelete?: () => void;
  onBulkExport?: () => void;
  onBulkAssign?: () => void;
  className?: string;
}

export function BulkActions({
  userRole,
  resource,
  selectedCount,
  onBulkEdit,
  onBulkDelete,
  onBulkExport,
  onBulkAssign,
  className = ''
}: BulkActionsProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const canBulkOperations = canPerformAction(userRole, 'bulk', 'manage');
  const canDelete = canPerformAction(userRole, resource, 'delete');
  const canUpdate = canPerformAction(userRole, resource, 'update');

  if (!canBulkOperations || selectedCount === 0) {
    return null;
  }

  const handleBulkDelete = () => {
    if (onBulkDelete) {
      onBulkDelete();
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg ${className}`}>
      <span className="text-sm font-medium text-blue-900">
        {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
      </span>

      {/* Bulk Edit */}
      {canUpdate && onBulkEdit && (
        <Button
          variant="outline"
          size="sm"
          onClick={onBulkEdit}
          className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit Selected
        </Button>
      )}

      {/* Bulk Assign */}
      {canUpdate && onBulkAssign && (
        <Button
          variant="outline"
          size="sm"
          onClick={onBulkAssign}
          className="text-green-600 hover:text-green-700 hover:bg-green-100"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Assign
        </Button>
      )}

      {/* Bulk Export */}
      {onBulkExport && (
        <Button
          variant="outline"
          size="sm"
          onClick={onBulkExport}
          className="text-purple-600 hover:text-purple-700 hover:bg-purple-100"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export Selected
        </Button>
      )}

      {/* Bulk Delete */}
      {canDelete && onBulkDelete && (
        <>
          {!showDeleteConfirm ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              className="text-red-600 hover:text-red-700 hover:bg-red-100"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Selected
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-600 font-medium">Delete {selectedCount} items?</span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkDelete}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                Confirm
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-600 hover:text-gray-700"
              >
                Cancel
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}